import {useCallback, useMemo} from 'react';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import type {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {
  type ColumnConstraint,
  type InsertableColumnInfo,
} from 'notebook/tables/constants/TableColumnConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableOperation} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {useNullableTableNoteContext} from 'notebook/tables/store/contexts/TableNoteContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {clearCellsInRange, clearSelection} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {
  deleteColumns,
  insertColumnsLeft,
  insertColumnsRight,
  renameColumn,
  setColumnConstraint,
} from 'notebook/tables/store/logic/mutations/columnOps';
import {deleteTable} from 'notebook/tables/store/logic/mutations/deleteTable';
import {performDoubleClickFillDown, performFillDown} from 'notebook/tables/store/logic/mutations/fillDown';
import {type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {pasteTable, type PasteTableOptions} from 'notebook/tables/store/logic/mutations/pasteTable';
import {reorderRowsOrColumns} from 'notebook/tables/store/logic/mutations/reorderRowsOrColumns';
import {deleteRows, insertRowsAbove, insertRowsBelow} from 'notebook/tables/store/logic/mutations/rowOps';
import {saveSelection, setMultiIndices} from 'notebook/tables/store/logic/mutations/selection';
import {toggleHeaderCol, toggleHeaderRow} from 'notebook/tables/store/logic/mutations/toggleHeader';
import {updateCell} from 'notebook/tables/store/logic/mutations/updateCell';
import {type MergeValidationResult, validateMerge} from 'notebook/tables/store/logic/mutations/validateMerge';

export type {PasteTableOptions} from 'notebook/tables/store/logic/mutations/pasteTable';
export type {MergeValidationResult} from 'notebook/tables/store/logic/mutations/validateMerge';

interface TableOpsMethods {
  renameTable: (tableName: string, newTableName: string) => void;
  performTableOp?: (tableOp: TableOperation) => void;
  deleteRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  mergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  /**
   * Reorder rows/columns of the table via the underlying table op. This is the raw
   * op used to reorder single-input columns; drag-to-reorder of regular rows/columns
   * goes through {@link TableMutationMethods.reorderRowsOrColumns}.
   */
  reorderSingleInputs: (tableName: string, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;

  /**
   * Clear all cells within the current selection, expanding to fully contain any
   * intersecting merged regions.
   *
   * Checkpoints the current selection for undo, clears the cells, and leaves cell
   * editing. No-op when there is no real selection.
   */
  clearSelection: () => void;

  /**
   * Delete a contiguous range of rows.
   *
   * Checkpoints the deleted rows as the selection for undo, then deletes them. `end`
   * defaults to `start`; no-op when `start` is null.
   */
  deleteRows: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows above the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsAbove: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows below the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsBelow: (start: number | null, end: number | null) => void;

  /**
   * Delete a contiguous range of columns.
   *
   * Checkpoints the deleted columns as the selection for undo, then deletes them. `end` defaults to
   * `start`; no-op when `start` is null or when the table does not allow columns to be added and
   * removed.
   */
  deleteColumns: (start: number | null, end: number | null) => void;

  /**
   * Insert as many columns to the left of the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
   * no-op when `start` is null or when the table does not allow columns to be added and removed.
   */
  insertColumnsLeft: (
    start: number | null,
    end: number | null,
    columnInfo?: InsertableColumnInfo | {}
  ) => void;

  /**
   * Insert as many columns to the right of the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
   * no-op when `start` is null or when the table does not allow columns to be added and removed.
   */
  insertColumnsRight: (
    start: number | null,
    end: number | null,
    columnInfo?: InsertableColumnInfo | {}
  ) => void;

  /**
   * Rename the column at `index`, checkpointing the current selection for undo first.
   */
  renameColumn: (index: number, name: string) => void;

  /**
   * Set the constraint of the column at `index`, optionally merging `headerRowCell` into that
   * column's header cell, checkpointing the current selection for undo first.
   */
  setColumnConstraint: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell?: Partial<Cell>
  ) => void;

  /**
   * Delete the whole table via the embedding surface's delete handler.
   *
   * Only valid when {@link TableModel.canDeleteTable} is true; callers gate on it.
   */
  deleteTable: () => void;

  /**
   * Toggle the header column flag on the table, clearing bold formatting on the first column.
   */
  toggleHeaderCol: () => void;

  /**
   * Toggle the header row flag on the table, clearing bold formatting on the first row.
   */
  toggleHeaderRow: () => void;

  /**
   * Validate whether the current selection can be merged into a single cell.
   *
   * Reads the merge-expanded selection box from the model and reports whether the
   * selection straddles the freeze boundary (merging disallowed) and whether the
   * merge would discard cell values (caller should confirm). Returns `null` when
   * there is no active selection.
   *
   * The merge itself is performed separately via `mergeRegion`; the confirmation
   * modal is owned by the component.
   */
  validateMerge: () => MergeValidationResult | null;

  /**
   * Paste a 2D array of cells into the table.
   *
   * Orchestrates the full paste pipeline (excluding clipboard parsing and file
   * attachment, which remain in the component): resolves the destination
   * selection, tiles/reshapes the pasted cells to fill it, validates the paste
   * (surfacing warnings/errors via notifications), normalizes typed values,
   * asynchronously resolves selectors, writes the cells via the pure
   * `pasteTable` mutation, enforces capacity limits, remerges single-input
   * columns, persists the change, and tracks analytics.
   *
   * @param cells - The 2D array of cells to paste (already parsed from the clipboard).
   * @param options - Paste options: `transpose` and the originating `selectionIndex`.
   * @param setSelection - Component callback to update the multi-selection. Called
   *   once with the expanded paste region (so a rejected paste is visible) and again
   *   with the final pasted region.
   */
  pasteTable: (
    cells: Array<Array<PastedCell>>,
    options: PasteTableOptions,
    setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
  ) => void;

  /**
   * Set the table's multi-selection anchor (`start`) and end indices.
   *
   * Writes the selection indices to the store and syncs the spreadsheet editing
   * state for the anchor cell. When `text` is omitted, the anchor cell's current
   * text / typed value are read from the table.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward when needed.
   *
   * @param indices - The `start` (anchor) and `end` indices, plus optional `text`
   *   to seed the editing state for the anchor cell
   * @param options - Optional `multiSelIndex` to keep as the selected index;
   *   defaults to the `start` anchor when omitted
   */
  setMultiIndices: (
    indices: {start: TableIndex; end: TableIndex; text?: string},
    options?: {multiSelIndex?: TableIndex | null}
  ) => void;

  /**
   * Checkpoint the current selection onto the undo/redo restore history.
   *
   * Snapshots the current multi-selection state (or an explicit override) and
   * pushes it via the table's `storeState` handler. Typically called before a
   * structural edit so the prior selection can be restored on undo.
   *
   * @param override - Optional selection state to snapshot instead of the
   *   current store state
   */
  saveSelection: (
    override?: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData}
  ) => void;

  /**
   * Complete a drag-to-reorder of rows or columns.
   *
   * Encapsulates the full `onDragComplete` flow: reads the in-progress drag
   * dimension and multi-selection from the model, clears the dragging state,
   * rejects (surfacing an error notification) when the drop position would split
   * a merged cell, checkpoints the selection for undo via `saveSelection`,
   * adjusts the frozen-column count for column reorders, performs the reorder via
   * the `reorderSingleInputs` op (skipped when the drop is a no-op), updates the
   * selection to the reordered range via `setMultiIndices`, and tracks analytics.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward.
   *
   * @param insertIndex - The index the dragged range is dropped before.
   */
  reorderRowsOrColumns: (insertIndex: number) => void;

  /**
   * Fill down the current selection into the active fill-down box.
   *
   * Checks fill-down locking (surfacing a warning notification and aborting when
   * locked), reads the source selection (`expandedBox`) and destination
   * (`fillDownBox`) from the models, checkpoints the current selection for undo
   * via `saveSelection`, extrapolates via the pure `fillDown` mutation,
   * persists the change, and updates the selection to the filled region via
   * `setMultiIndices`. No-op when there is no active selection or fill-down box.
   */
  performFillDown: () => void;

  /**
   * Fill down the current selection through the following blank rows, triggered
   * by double-clicking the fill handle.
   *
   * Reads the source selection (`expandedBox`) and the double-click destination
   * (`fillDown.doubleClickBox`) from the models, extrapolates via the pure
   * `doubleClickFillDown` mutation, persists the change, and updates the
   * selection to the filled region via `setMultiIndices`. No-op when the
   * selection contains a merged region.
   */
  performDoubleClickFillDown: () => void;
}

/**
 * Hook that provides table mutation methods with analytics and persistence.
 *
 * All data is fetched fresh from the TableModel at execution time, ensuring we always use the latest
 * state and keeping the returned methods stable. Each method is a plain function living in
 * `store/logic/mutations/`; this hook builds the shared {@link TableMutationParams} bundle once and
 * binds it into each method.
 */
export function useTableMutations(): TableMutationMethods {
  const {useTableEditableStore, tableModelSelector, setState} = TableModelContext.useContext();
  const tableOps = useTableOps();
  const {tableModel: disableMergingCells} = useTableModel((state) => state.props.disableMergingCells);
  // Nullable so merge validation still works in isolated contexts (tests/harnesses) that do not provide a
  // note context; in that case only the edited table is scanned for subordinate-cell references.
  const tableNoteContext = useNullableTableNoteContext();

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  const params = useMemo<TableMutationParams>(
    () => ({getTableModel, setState, tableNoteContext, tableOps}),
    [getTableModel, setState, tableNoteContext, tableOps]
  );

  return useMemo<TableMutationMethods>(
    () => ({
      updateCell: (cellIndex, cellData, expandTable) => {
        updateCell(params, cellIndex, cellData, expandTable);
      },
      clearCellsInRange: (selectionBox) => {
        clearCellsInRange(params, selectionBox);
      },
      clearSelection: () => {
        clearSelection(params);
      },
      deleteRows: (start, end) => {
        deleteRows(params, start, end);
      },
      insertRowsAbove: (start, end) => {
        insertRowsAbove(params, start, end);
      },
      insertRowsBelow: (start, end) => {
        insertRowsBelow(params, start, end);
      },
      deleteColumns: (start, end) => {
        deleteColumns(params, start, end);
      },
      insertColumnsLeft: (start, end, columnInfo) => {
        insertColumnsLeft(params, start, end, columnInfo);
      },
      insertColumnsRight: (start, end, columnInfo) => {
        insertColumnsRight(params, start, end, columnInfo);
      },
      renameColumn: (index, name) => {
        renameColumn(params, index, name);
      },
      setColumnConstraint: (index, columnConstraint, headerRowCell) => {
        setColumnConstraint(params, index, columnConstraint, headerRowCell);
      },
      deleteTable: () => {
        deleteTable(params);
      },
      toggleHeaderCol: () => {
        toggleHeaderCol(params);
      },
      toggleHeaderRow: () => {
        toggleHeaderRow(params);
      },
      validateMerge: () => validateMerge(params),
      pasteTable: (cells, options, setSelection) => {
        pasteTable(params, cells, options, setSelection);
      },
      setMultiIndices: (indices, options) => {
        setMultiIndices(params, indices, options);
      },
      saveSelection: (override) => {
        saveSelection(params, override);
      },
      reorderRowsOrColumns: (insertIndex) => {
        reorderRowsOrColumns(params, insertIndex);
      },
      performFillDown: () => {
        performFillDown(params);
      },
      performDoubleClickFillDown: () => {
        performDoubleClickFillDown(params);
      },
      renameTable: tableOps.renameTable,
      performTableOp: tableOps.performTableOp,
      deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
      mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
      unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
      reorderSingleInputs: tableOps.reorderTableRowsOrColumns,
    }),
    [params, tableOps, disableMergingCells]
  );
}
